import React from 'react';
import { motion } from 'framer-motion';
import BlueHeaderSection from '../BlueHeader';

// Asset Imports
import mobileImg from "../../asset/images/mobile-dev.png";
import mobileImg1 from "../../asset/images/mobile-dev1.png";
import mobileImg2 from "../../asset/images/mobile-dev2.png";
import mobileImg3 from "../../asset/images/mobile-dev3.png";

// Shared Animation Constant
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.4, ease: 'easeOut' } 
  }
};

const services = [
  {
    id: "mob-svc-1",
    title: 'iOS App Development',
    text: 'Custom iPhone and iPad apps built with Swift and Objective-C, featuring refined UX and full App Store readiness.',
  },
  {
    id: "mob-svc-2",
    title: 'Android App Development',
    text: 'Robust, feature-rich Android applications optimized for performance across devices using Kotlin and Java.',
  },
  {
    id: "mob-svc-3",
    title: 'Cross-Platform Development',
    text: 'Build once, deploy everywhere with React Native apps that reduce time and cost without compromising quality.',
  },
  {
    id: "mob-svc-4",
    title: 'UI/UX Design for Mobile',
    text: 'We design intuitive, engaging interfaces that keep users coming back, using Figma and native platform guidelines.',
  },
  {
    id: "mob-svc-5",
    title: 'Backend & API Development',
    text: 'Powerful, secure backends built with Node.js, Firebase, or AWS to support real-time and data-heavy applications.',
  },
  {
    id: "mob-svc-6",
    title: 'App Maintenance & Support',
    text: 'Ongoing monitoring, security updates, and feature enhancements to keep your app running smoothly post-launch.',
  }
];

const features = [
  {
    id: "mob-feat-1",
    title: "End-to-End Development",
    description: "From product discovery and UX to development, testing, and launch, we handle the entire lifecycle as a single accountable partner.",
    image: mobileImg1
  },
  {
    id: "mob-feat-2",
    title: "Security-First Approach",
    description: "We follow best practices for authentication, encryption, and secure coding to protect user data and comply with industry standards.",
    image: mobileImg2
  },
  {
    id: "mob-feat-3",
    title: "Agile Delivery",
    description: "Our agile processes ensure faster iterations, transparent communication, and the flexibility to adapt to changing requirements.",
    image: mobileImg3
  },
  {
    id: "mob-feat-4",
    title: "Scalable Architecture",
    description: "We engineer apps and backends to handle growth gracefully, from early-stage MVPs to large user bases.",
    image: mobileImg
  },
];

const MobileAppDevelopment = () => {
  return (
    <div className="overflow-hidden">
      <BlueHeaderSection heading="MOBILE APP DEVELOPMENT" url="Mobile App Development" showServices={true} />

      {/* Intro Section */}
      <section className="py-12 md:py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Crafting Scalable Apps for a Mobile-First World
            </h2>
            <div className="w-24 h-1 bg-[#f7941d] mx-auto"></div>
          </div>

          <div className="text-center mb-16">
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
              At **BytesFarms Technologies**, we don't just build apps — we create digital experiences that connect brands with users through seamless design and powerful functionality.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="p-6 rounded-2xl bg-white shadow-md border-l-4 border-[#f7941d] hover:shadow-lg transition-all duration-300"
              >
                <h4 className="text-xl font-semibold text-[#f7941d] mb-3">{item.title}</h4>
                <p className="text-gray-800 leading-relaxed tracking-tight">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section - Fixed Duplication */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why choose BytesFarms for Mobile App Development?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {features.map((feature) => (
              <motion.div
                key={feature.id} // Fixed: Unique key prevents ghosting/double rendering
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="group relative overflow-hidden rounded-2xl shadow-2xl h-[220px] md:h-[250px] bg-gray-900"
              >
                <img
                  src={feature.image}
                  alt="" // Set to empty string if the title is already in the H3 below to avoid screen-reader duplication
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 group-hover:bg-black/75 transition-opacity duration-300"></div>
                
                <div className="relative z-10 p-6 md:p-8 h-full flex flex-col justify-center text-center">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobileAppDevelopment;